import React, { useRef } from "react";
import { Button } from "../../components/ui/button";
import { Download } from "lucide-react";
import { formatCurrency, formatDate } from "../../lib/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const QuotePDFTemplate = ({ quote, tenant, billingSettings, quoteSettings, onExport }) => {
    const templateRef = useRef(null);

    const company = billingSettings?.company_info || {};
    const branding = quoteSettings?.branding || {};
    const signature = quoteSettings?.signature || {};
    const footer = quoteSettings?.footer || {};
    const introduction = quoteSettings?.introduction || {};
    const commercialTerms = quoteSettings?.commercial_terms || {};
    const clauses = quoteSettings?.clauses || {};

    const primaryColor = branding.primary_color || "#1E3A8A";
    const companyName = company.legal_name || company.commercial_name || tenant?.name || "Orkestria";
    const validityDays = commercialTerms.validity_days || 30;

    const handleExport = async () => {
        if (!templateRef.current) return;
        try {
            const canvas = await html2canvas(templateRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
            });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save(`devis-${quote.quote_number}.pdf`);
            if (onExport) onExport();
        } catch (error) {
            console.error("PDF export error:", error);
        }
    };

    return (
        <div className="space-y-4">
            <Button onClick={handleExport} className="w-full" data-testid="export-quote-pdf">
                <Download className="w-4 h-4 mr-2" />
                Télécharger le PDF
            </Button>

            <div
                ref={templateRef}
                className="bg-white text-black p-8 max-w-[210mm] mx-auto"
                style={{ fontFamily: "Arial, sans-serif" }}
            >
                {/* Header */}
                <div className="flex justify-between items-start mb-8 pb-4 border-b-2" style={{ borderColor: primaryColor }}>
                    <div>
                        <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>DEVIS</h1>
                        <p className="text-lg font-semibold mt-1">{quote.quote_number}</p>
                    </div>
                    <div className="text-right">
                        {company.logo_url && branding.show_logo !== false && (
                            <img src={company.logo_url} alt="Logo" className="h-12 mb-2 ml-auto" />
                        )}
                        <h2 className="text-xl font-bold" style={{ color: primaryColor }}>{companyName}</h2>
                        {company.address && <p className="text-sm text-gray-600">{company.address}</p>}
                        {company.email && <p className="text-sm text-gray-600">{company.email}</p>}
                        {company.phone && <p className="text-sm text-gray-600">{company.phone}</p>}
                        {company.website && <p className="text-sm text-gray-600">{company.website}</p>}
                        {company.business_number && <p className="text-sm text-gray-600">N°: {company.business_number}</p>}
                        {company.tax_number && <p className="text-sm text-gray-600">TVA: {company.tax_number}</p>}
                    </div>
                </div>

                {/* Client & Date Info */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div className="bg-gray-50 p-4 rounded">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Client</p>
                        <p className="font-bold text-lg">{quote.client_name || "Client"}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded text-right">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Date du devis</p>
                        <p className="font-bold">{formatDate(quote.created_at)}</p>
                        <p className="text-xs text-gray-500 uppercase font-semibold mt-2 mb-1">Valide jusqu'au</p>
                        <p className="font-bold">
                            {quote.valid_until
                                ? formatDate(quote.valid_until)
                                : formatDate(
                                      new Date(new Date(quote.created_at).getTime() + validityDays * 86400000).toISOString()
                                  )}
                        </p>
                    </div>
                </div>

                {/* Introduction */}
                {introduction.default_text && (
                    <div className="mb-6 text-sm text-gray-700">
                        <p>{introduction.default_text}</p>
                    </div>
                )}

                {/* Items Table */}
                <table className="w-full mb-8">
                    <thead>
                        <tr style={{ backgroundColor: primaryColor }} className="text-white">
                            <th className="text-left p-3 font-semibold">Description</th>
                            <th className="text-center p-3 font-semibold w-20">Qté</th>
                            <th className="text-right p-3 font-semibold w-32">Prix unit.</th>
                            <th className="text-right p-3 font-semibold w-32">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {quote.items?.map((item, index) => (
                            <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                <td className="p-3">{item.description}</td>
                                <td className="text-center p-3">{item.quantity}</td>
                                <td className="text-right p-3 font-mono">{formatCurrency(item.unit_price)}</td>
                                <td className="text-right p-3 font-mono font-semibold">
                                    {formatCurrency(item.quantity * item.unit_price)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                    <div className="w-72">
                        <div className="flex justify-between py-2 border-b">
                            <span>Sous-total HT</span>
                            <span className="font-mono">{formatCurrency(quote.subtotal)}</span>
                        </div>
                        {quote.discount_amount > 0 && (
                            <div className="flex justify-between py-2 border-b text-green-600">
                                <span>Remise ({quote.discount_percent}%)</span>
                                <span className="font-mono">-{formatCurrency(quote.discount_amount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between py-2 border-b">
                            <span>TVA (20%)</span>
                            <span className="font-mono">{formatCurrency(quote.tax_amount)}</span>
                        </div>
                        <div className="flex justify-between py-3 text-white px-3 rounded mt-2" style={{ backgroundColor: primaryColor }}>
                            <span className="font-bold text-lg">Total TTC</span>
                            <span className="font-mono font-bold text-lg">{formatCurrency(quote.total)}</span>
                        </div>
                    </div>
                </div>

                {/* Deposit */}
                {commercialTerms.deposit_required && commercialTerms.deposit_percentage && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                        <p className="font-semibold">Acompte requis : {commercialTerms.deposit_percentage}% soit {formatCurrency(quote.total * commercialTerms.deposit_percentage / 100)}</p>
                        {commercialTerms.payment_terms && <p className="mt-1 text-gray-600">{commercialTerms.payment_terms}</p>}
                    </div>
                )}

                {/* Execution Delay */}
                {commercialTerms.execution_delay && (
                    <div className="mt-4 text-sm text-gray-700">
                        <p><strong>Délai d'exécution :</strong> {commercialTerms.execution_delay}</p>
                    </div>
                )}

                {/* Notes */}
                {quote.notes && (
                    <div className="mt-6 p-4 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Notes</p>
                        <p className="text-sm">{quote.notes}</p>
                    </div>
                )}

                {/* Clauses */}
                {(clauses.general_conditions || clauses.specific_clauses || clauses.commitments) && (
                    <div className="mt-6 text-xs text-gray-600 space-y-2">
                        {clauses.general_conditions && (
                            <div>
                                <p className="font-semibold text-gray-500 uppercase mb-1">Conditions Générales</p>
                                <p>{clauses.general_conditions}</p>
                            </div>
                        )}
                        {clauses.specific_clauses && (
                            <div>
                                <p className="font-semibold text-gray-500 uppercase mb-1">Clauses Spécifiques</p>
                                <p>{clauses.specific_clauses}</p>
                            </div>
                        )}
                        {clauses.commitments && (
                            <div>
                                <p className="font-semibold text-gray-500 uppercase mb-1">Engagements</p>
                                <p>{clauses.commitments}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Signature */}
                {(signature.signer_name || signature.signer_title) && (
                    <div className="mt-8 flex justify-end">
                        <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Signature</p>
                            {signature.signature_image_url && (
                                <img src={signature.signature_image_url} alt="Signature" className="h-16 mb-2 ml-auto" />
                            )}
                            {signature.signer_name && <p className="font-semibold">{signature.signer_name}</p>}
                            {signature.signer_title && <p className="text-sm text-gray-600">{signature.signer_title}</p>}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="mt-12 pt-4 border-t text-center text-xs text-gray-500">
                    {footer.thank_you_message && <p>{footer.thank_you_message}</p>}
                    {footer.legal_mention && <p className="mt-1">{footer.legal_mention}</p>}
                    {footer.custom_text && <p className="mt-1">{footer.custom_text}</p>}
                    <p className="mt-1">Ce devis est valable {validityDays} jours à compter de sa date d'émission.</p>
                    {footer.show_orkestria_mention !== false && (
                        <p className={`mt-1 ${footer.orkestria_mention_discrete ? "text-[10px]" : ""}`}>
                            {companyName}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const InvoicePDFTemplate = ({ invoice, tenant, billingSettings, onExport }) => {
    const templateRef = useRef(null);

    const company = billingSettings?.company_info || {};
    const paymentInfo = billingSettings?.payment_info || {};
    const paymentTerms = billingSettings?.payment_terms || {};
    const invoiceFooter = billingSettings?.invoice_footer || {};
    const template = billingSettings?.invoice_template || {};

    const primaryColor = template.primary_color || "#1E3A8A";
    const companyName = company.legal_name || company.commercial_name || tenant?.name || "";

    const handleExport = async () => {
        if (!templateRef.current) return;
        try {
            const canvas = await html2canvas(templateRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
            });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save(`facture-${invoice.invoice_number}.pdf`);
            if (onExport) onExport();
        } catch (error) {
            console.error("PDF export error:", error);
        }
    };

    const isPaid = invoice.status === "paid";
    const isOverdue = invoice.due_date && new Date(invoice.due_date) < new Date() && !isPaid;

    return (
        <div className="space-y-4">
            <Button onClick={handleExport} className="w-full" data-testid="export-invoice-pdf">
                <Download className="w-4 h-4 mr-2" />
                Télécharger le PDF
            </Button>

            <div
                ref={templateRef}
                className="bg-white text-black p-8 max-w-[210mm] mx-auto"
                style={{ fontFamily: "Arial, sans-serif" }}
            >
                {/* Header */}
                <div className="flex justify-between items-start mb-8 pb-4 border-b-2" style={{ borderColor: primaryColor }}>
                    <div>
                        <h1 className="text-3xl font-bold" style={{ color: primaryColor }}>FACTURE</h1>
                        <p className="text-lg font-semibold mt-1">{invoice.invoice_number}</p>
                        {isPaid && (
                            <div className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-700 font-bold text-sm rounded">
                                PAYÉE
                            </div>
                        )}
                        {isOverdue && (
                            <div className="mt-2 inline-block px-3 py-1 bg-red-100 text-red-700 font-bold text-sm rounded">
                                EN RETARD
                            </div>
                        )}
                    </div>
                    <div className="text-right">
                        {company.logo_url && template.show_logo !== false && (
                            <img src={company.logo_url} alt="Logo" className="h-12 mb-2 ml-auto" />
                        )}
                        <h2 className="text-xl font-bold" style={{ color: primaryColor }}>{companyName}</h2>
                        {company.address && <p className="text-sm text-gray-600">{company.address}</p>}
                        {company.email && <p className="text-sm text-gray-600">{company.email}</p>}
                        {company.phone && <p className="text-sm text-gray-600">{company.phone}</p>}
                        {company.website && <p className="text-sm text-gray-600">{company.website}</p>}
                        {company.business_number && <p className="text-sm text-gray-600">N°: {company.business_number}</p>}
                        {company.tax_number && <p className="text-sm text-gray-600">TVA: {company.tax_number}</p>}
                    </div>
                </div>

                {/* Client & Date Info */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div className="bg-gray-50 p-4 rounded">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Facturé à</p>
                        <p className="font-bold text-lg">{invoice.client_name || "Client"}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded text-right">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Date de facturation</p>
                        <p className="font-bold">{formatDate(invoice.created_at)}</p>
                        {invoice.due_date && (
                            <>
                                <p className="text-xs text-gray-500 uppercase font-semibold mt-2 mb-1">Échéance</p>
                                <p className={`font-bold ${isOverdue ? "text-red-600" : ""}`}>
                                    {formatDate(invoice.due_date)}
                                </p>
                            </>
                        )}
                    </div>
                </div>

                {/* Items Table */}
                <table className="w-full mb-8">
                    <thead>
                        <tr style={{ backgroundColor: primaryColor }} className="text-white">
                            <th className="text-left p-3 font-semibold">Description</th>
                            <th className="text-center p-3 font-semibold w-20">Qté</th>
                            <th className="text-right p-3 font-semibold w-32">Prix unit.</th>
                            <th className="text-right p-3 font-semibold w-32">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.items?.map((item, index) => (
                            <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                                <td className="p-3">{item.description}</td>
                                <td className="text-center p-3">{item.quantity}</td>
                                <td className="text-right p-3 font-mono">{formatCurrency(item.unit_price)}</td>
                                <td className="text-right p-3 font-mono font-semibold">
                                    {formatCurrency(item.quantity * item.unit_price)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                    <div className="w-72">
                        <div className="flex justify-between py-2 border-b">
                            <span>Sous-total HT</span>
                            <span className="font-mono">{formatCurrency(invoice.subtotal)}</span>
                        </div>
                        {invoice.discount_amount > 0 && (
                            <div className="flex justify-between py-2 border-b text-green-600">
                                <span>Remise ({invoice.discount_percent}%)</span>
                                <span className="font-mono">-{formatCurrency(invoice.discount_amount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between py-2 border-b">
                            <span>TVA (20%)</span>
                            <span className="font-mono">{formatCurrency(invoice.tax_amount)}</span>
                        </div>
                        <div className="flex justify-between py-3 text-white px-3 rounded mt-2" style={{ backgroundColor: primaryColor }}>
                            <span className="font-bold text-lg">Total TTC</span>
                            <span className="font-mono font-bold text-lg">{formatCurrency(invoice.total)}</span>
                        </div>
                        {invoice.amount_paid > 0 && (
                            <>
                                <div className="flex justify-between py-2 mt-2 border-b text-green-600">
                                    <span>Montant payé</span>
                                    <span className="font-mono">{formatCurrency(invoice.amount_paid)}</span>
                                </div>
                                <div className="flex justify-between py-2 font-bold">
                                    <span>Reste à payer</span>
                                    <span className="font-mono">{formatCurrency(invoice.total - invoice.amount_paid)}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Notes */}
                {invoice.notes && (
                    <div className="mt-8 p-4 bg-gray-50 rounded">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Notes</p>
                        <p className="text-sm">{invoice.notes}</p>
                    </div>
                )}

                {/* Payment Info */}
                <div className="mt-8 p-4 rounded" style={{ backgroundColor: `${primaryColor}10` }}>
                    <p className="text-xs uppercase font-semibold mb-2" style={{ color: primaryColor }}>Informations de paiement</p>
                    {paymentInfo.beneficiary_name && (
                        <p className="text-sm">Bénéficiaire : {paymentInfo.beneficiary_name}</p>
                    )}
                    {paymentInfo.bank_name && (
                        <p className="text-sm">Banque : {paymentInfo.bank_name}</p>
                    )}
                    {paymentInfo.iban && (
                        <p className="text-sm">IBAN : {paymentInfo.iban}</p>
                    )}
                    {paymentInfo.bic && (
                        <p className="text-sm">BIC : {paymentInfo.bic}</p>
                    )}
                    {!paymentInfo.beneficiary_name && !paymentInfo.iban && (
                        <p className="text-sm">Merci de régler cette facture par virement bancaire à l'ordre de {companyName}.</p>
                    )}
                    <p className="text-sm mt-1">Référence à indiquer: {invoice.invoice_number}</p>
                    {paymentInfo.custom_instructions && (
                        <p className="text-sm mt-2 text-gray-600">{paymentInfo.custom_instructions}</p>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500">
                    {invoiceFooter.thank_you_message && <p>{invoiceFooter.thank_you_message}</p>}
                    {invoiceFooter.legal_mention && <p className="mt-1">{invoiceFooter.legal_mention}</p>}
                    {paymentTerms.late_penalty_enabled && paymentTerms.late_penalty_text && (
                        <p className="mt-1">{paymentTerms.late_penalty_text}</p>
                    )}
                    {paymentTerms.late_penalty_enabled && !paymentTerms.late_penalty_text && (
                        <p className="mt-1">
                            En cas de retard de paiement, des pénalités de {paymentTerms.late_penalty_rate || 3}% seront appliquées.
                        </p>
                    )}
                    {invoiceFooter.custom_text && <p className="mt-1">{invoiceFooter.custom_text}</p>}
                    {invoiceFooter.show_orkestria_mention !== false && (
                        <p className={`mt-1 ${invoiceFooter.orkestria_mention_discrete ? "text-[10px]" : ""}`}>
                            {companyName}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
